import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button, Input, Label, cn } from "@bloom/ui";
import { Loader2 } from "lucide-react";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";

// Mirrors the admin login's demo chip row. All six seeded demo accounts can
// also sign into the customer site (Better-Auth is the shared backend), so we
// surface every role — handy when showing "the same admin can browse like a
// regular viewer." Hidden in production builds.
const DEMO_PASSWORD = "BloomDev!2026";
const DEMO_ROLES = [
  { role: "user", label: "Viewer" },
  { role: "support", label: "Support" },
  { role: "content_manager", label: "Content" },
  { role: "finance", label: "Finance" },
  { role: "admin", label: "Admin" },
  { role: "superadmin", label: "Superadmin" },
] as const;
const demoEmailFor = (role: string) => `${role.replace(/_/g, "-")}@bloom.dev`;

export function LoginPage() {
  const { t } = useLocale();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingDemo, setPendingDemo] = useState<string | null>(null);
  const [showDemo, setShowDemo] = useState(false);

  // Bloom is in staging; the demo chips ship on every deploy so reviewers
  // can sign in without creating accounts. Flip VITE_DISABLE_DEMO_LOGINS=true
  // when launching to real public traffic.
  const isDev = import.meta.env.VITE_DISABLE_DEMO_LOGINS !== "true";

  const signInAs = async (creds: { email: string; password: string }) => {
    setError(null);
    const res = await fetch("/api/auth/sign-in/email", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(creds),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(body.message ?? `Login failed (${res.status})`);
    }
    await refresh();
    navigate(next);
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signInAs({ email, password });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onDemoClick = async (role: string) => {
    setPendingDemo(role);
    try {
      await signInAs({ email: demoEmailFor(role), password: DEMO_PASSWORD });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPendingDemo(null);
    }
  };

  return (
    <AuthShell heading={t("auth.login")}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        {error ? <p className="text-sm text-[var(--brand-rose)]">{error}</p> : null}
        <Button type="submit" className="w-full" disabled={submitting || pendingDemo !== null}>
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            t("auth.continue")
          )}
        </Button>
      </form>
      <p className="text-center text-sm text-[var(--brand-muted)]">
        <Link to="/forgot-password" className="hover:underline">
          {t("auth.forgot_password")}
        </Link>
      </p>
      <p className="text-center text-sm">
        {t("auth.dont_have_account")}{" "}
        <Link to="/signup" className="font-semibold text-[var(--brand-rose)]">
          {t("auth.signup")}
        </Link>
      </p>

      {isDev ? (
        <div className="border-t border-[var(--brand-border)] pt-4 text-center">
          <button
            type="button"
            onClick={() => setShowDemo((v) => !v)}
            className="text-xs font-medium text-[var(--brand-muted)] transition-colors hover:text-[var(--brand-rose)]"
          >
            {showDemo ? "Hide demo logins" : "Show demo logins"}
          </button>
          {showDemo ? (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-[var(--brand-muted)]">
              {DEMO_ROLES.map((d, i) => (
                <span key={d.role} className="contents">
                  {i > 0 ? <span className="text-[var(--brand-border)]">·</span> : null}
                  <button
                    type="button"
                    onClick={() => onDemoClick(d.role)}
                    disabled={submitting || pendingDemo !== null}
                    className={cn(
                      "underline decoration-dotted underline-offset-4 transition-colors hover:text-[var(--brand-rose)] disabled:opacity-40",
                      pendingDemo === d.role && "text-[var(--brand-rose)]",
                    )}
                  >
                    {pendingDemo === d.role ? (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        {d.label}
                      </span>
                    ) : (
                      d.label
                    )}
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </AuthShell>
  );
}

export function AuthShell({
  heading,
  children,
  variant = "login",
}: {
  heading: string;
  children: React.ReactNode;
  /** Picks the hero photo on the left panel. Both variants share the same
   *  ink-from-bottom gradient + tagline copy. */
  variant?: "login" | "signup";
}) {
  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <aside
        className="relative isolate hidden overflow-hidden bg-[var(--brand-ink)] p-12 lg:flex lg:flex-col lg:justify-end"
        style={{
          backgroundImage: `url(/auth-imagery/${variant}.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Darker at the bottom so the tagline reads cleanly on any image; soft
            rose-tinted radial in the top-left ties the photo back to the brand. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background:radial-gradient(36rem_24rem_at_-10%_-10%,rgba(233,75,122,0.35),transparent_55%)]"
        />
        <div className="relative">
          <h2 className="font-display text-5xl text-white drop-shadow-md">Bloom</h2>
          <p className="mt-3 max-w-sm text-base text-white/80 drop-shadow">
            A place for feelings you can't put down.
          </p>
        </div>
      </aside>
      <section className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <h1 className="font-heading text-3xl font-bold">{heading}</h1>
          {children}
        </div>
      </section>
    </div>
  );
}
